import { fetchApi } from './client';
import type { PaginatedResponse, VendorAlias } from './types';

interface ListVendorAliasesParams {
    // Numeric catalog-product-candidate id — the canonical vendor key (F3).
    // Resolves candidate-only rows whose compound id degrades to `::<slug>`.
    // Backend prefers it over vendor_directory_id.
    catalog_product_candidate_id?: number;
    vendor_directory_id?: string;
    limit?: number;
    offset?: number;
}

/**
 * List vendor aliases, optionally scoped to a single vendor.
 */
export async function listVendorAliases(
    params: ListVendorAliasesParams = {},
): Promise<PaginatedResponse<VendorAlias>> {
    const searchParams = new URLSearchParams();

    if (params.catalog_product_candidate_id !== undefined) {
        searchParams.set(
            'catalog_product_candidate_id',
            String(params.catalog_product_candidate_id),
        );
    }

    if (params.vendor_directory_id) {
        searchParams.set('vendor_directory_id', params.vendor_directory_id);
    }

    if (params.limit !== undefined) {
        searchParams.set('limit', String(params.limit));
    }

    if (params.offset !== undefined) {
        searchParams.set('offset', String(params.offset));
    }

    const queryString = searchParams.toString();
    const path = `/api/vendor-aliases${queryString ? `?${queryString}` : ''}`;

    return fetchApi<PaginatedResponse<VendorAlias>>(path);
}

interface CreateVendorAliasParams {
    catalog_product_candidate_id: number;
    alias_type: string;
    alias_value: string;
}

export async function createVendorAlias(data: CreateVendorAliasParams): Promise<VendorAlias> {
    return fetchApi<VendorAlias>('/api/vendor-aliases', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

interface UpdateVendorAliasParams {
    alias_type?: string;
    alias_value?: string;
}

/** Edit an existing vendor alias (admin-only). At least one field is required. */
export async function updateVendorAlias(
    id: number,
    data: UpdateVendorAliasParams,
): Promise<VendorAlias> {
    return fetchApi<VendorAlias>(`/api/vendor-aliases/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
}

export async function deleteVendorAlias(id: number): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>(`/api/vendor-aliases/${String(id)}`, {
        method: 'DELETE',
    });
}
